import { useCallback, useMemo } from 'react';
import PencilIcon from '@ubnt/icons/PencilIcon';
import { useDevicePanel } from '@ubnt/link-core-ui/components/BottomPanel/context/DevicePanelContext';
import { deviceNames, type Device } from '@ubnt/link-core/modules/devices';
import { settingsCountrySelector } from 'modules/settings/slice';
import { useDispatch, useSelector } from 'app/store';
import {
  devicesCustomClientDeviceInstancesSelector,
  devicesCustomClientFormOpenChange,
  devicesCustomClientFormSetEditDevice,
  devicesDeviceChange,
  devicesDeviceRoleSelector,
} from 'modules/devices/slice';
import { trackEvent } from 'utils/googleAnalytics';
import { isRoleAp } from 'modules/devices/utils';
import { pricesSelector } from 'modules/prices/slice';
import { CLIENT_ID } from 'modules/devices/constants';

export const useDevicePicker = () => {
  const { id } = useDevicePanel();

  const role = useSelector((state) => (id ? devicesDeviceRoleSelector(state, id) : null));
  const country = useSelector(settingsCountrySelector);
  const externalDevices = useSelector(devicesCustomClientDeviceInstancesSelector);
  const isAp = isRoleAp(role);
  const priceList = useSelector(pricesSelector);
  const hidePrices = id === CLIENT_ID || Object.keys(priceList).length === 0;

  const dispatch = useDispatch();

  const onDeviceChange = useCallback(
    (name: string) => {
      if (id) {
        dispatch(devicesDeviceChange({ id, name, country }));
        trackEvent(isAp ? 'bottomPanelApDeviceChange' : 'bottomPanelClientDeviceChange', { name });
      }
    },
    [id, country, isAp, dispatch]
  );

  const onEditClick = useCallback(
    (name: string) => {
      dispatch(devicesCustomClientFormSetEditDevice(name));
      dispatch(devicesCustomClientFormOpenChange(true));
      trackEvent('clientFormAction', { action: 'edit' });
    },
    [dispatch]
  );

  const actions = useMemo(() => {
    const actionsMap: Record<
      string,
      { icon: React.ReactNode; action?: (name: string) => void; shouldClose?: boolean }[]
    > = {};
    externalDevices.forEach((device) => {
      if (!device.hidden) {
        actionsMap[device.name] = [
          {
            icon: (
              <PencilIcon aria-label={`Edit ${device.name}`} data-testid={`edit-client-${device.name}`} actionable />
            ),
            action: onEditClick,
            shouldClose: true,
          },
        ];
      }
    });
    return actionsMap;
  }, [externalDevices, onEditClick]);

  const itemNotesFn = useCallback((device: Device) => {
    if (device.supportsAfc) {
      return <span>* Long-range 6 GHz performance with AFC available in FCC/IC region only.</span>;
    }

    switch (device.name) {
      case deviceNames.U6_Extender:
        return (
          <span>
            * Cannot be set up standalone and requires UniFi WiFi managed by a UniFi Console or self-hosted UniFi
            Network Server.
          </span>
        );

      case deviceNames.U6_Mesh:
        return <span>* Weatherproof when installed LED side up with the included pole/wall mount accessory.</span>;

      case deviceNames.UAP_AC_Mesh:
        return <span>* UMA-D external antenna accessory offers directional coverage.</span>;

      default:
        return null;
    }
  }, []);

  return { onDeviceChange, actions, itemNotesFn, priceList, hidePrices };
};
